import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useTranslation } from "../context/LanguageContext";
import { track } from "../utils/analytics";

// Cross-promo for Penny Meals (pennymeals.com). Any paid DealHawk plan
// includes Penny Meals Plus; members sign in there with their DealHawk account.
// Mirrors pinto/components/PennyMealsCard.tsx. Dismissing it hides it in both
// placements for this browser.
const DISMISS_KEY = "dealhawk_penny_meals_card_dismissed";
const MARK_SRC = "/penny-meals-mark.png";
// One impression per placement per page load, so taps / views is a usable CTR
// even though the sidebar re-renders on every route change.
const viewedPlacements = new Set<string>();

function pennyMealsUrl(placement: "sidebar" | "more_sheet") {
  return `https://pennymeals.com/?utm_source=dealhawk&utm_medium=web&utm_campaign=${placement}`;
}

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

interface PennyMealsPromoProps {
  variant: "sidebar" | "sheet";
  isMember: boolean;
  collapsed?: boolean;
  fromPath: string;
}

export default function PennyMealsPromo({ variant, isMember, collapsed = false, fromPath }: PennyMealsPromoProps) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(readDismissed);
  const placement = variant === "sidebar" ? "sidebar" : "more_sheet";

  useEffect(() => {
    if (dismissed || viewedPlacements.has(placement)) return;
    viewedPlacements.add(placement);
    track("penny_meals_promo_viewed", { placement, isMember });
  }, [dismissed, placement, isMember]);

  if (dismissed) return null;

  const title = isMember ? t("nav.pennyMealsMemberTitle") : t("nav.pennyMealsFreeTitle");
  const body = isMember ? t("nav.pennyMealsMemberBody") : t("nav.pennyMealsFreeBody");
  const onOpen = () => {
    track("penny_meals_promo_tapped", { placement, isMember, fromPath });
  };
  const dismiss = () => {
    setDismissed(true);
    track("penny_meals_promo_dismissed", { placement, isMember, fromPath });
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // Storage blocked: the card stays hidden until the next page load.
    }
  };

  if (variant === "sheet") {
    return (
      <div
        className="relative mb-1 mt-2 flex items-center rounded-[14px] border border-[#fecaca] bg-[#fff1ef] dark:border-red-400/30 dark:bg-red-600/15"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <a
          href={pennyMealsUrl(placement)}
          target="_blank"
          rel="noopener"
          onClick={(e) => { e.stopPropagation(); onOpen(); }}
          className="flex min-w-0 flex-1 items-center gap-3 py-3 pl-3"
          aria-label={t("nav.pennyMealsShort")}
        >
          <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-white">
            <img src={MARK_SRC} alt="" className="h-[30px] w-[30px] object-contain" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[14.5px] font-extrabold">{title}</span>
            <span className="mt-0.5 block text-xs leading-4 text-gray-600 dark:text-dark-200">{body}</span>
          </span>
        </a>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); dismiss(); }}
          aria-label={t("nav.pennyMealsDismiss")}
          className="mx-1 flex h-11 w-11 flex-shrink-0 items-center justify-center self-start rounded-full text-[#9f1d1d] hover:bg-red-100 dark:text-red-300 dark:hover:bg-red-500/20"
        >
          <X size={18} />
        </button>
      </div>
    );
  }

  return (
    <div className="px-3 pb-3 relative z-10">
      <div className={`relative flex items-center rounded-xl border border-red-400/25 bg-red-500/10 transition-colors hover:bg-red-500/20 ${collapsed ? "md:border-transparent md:bg-transparent" : ""}`}>
        <a
          href={pennyMealsUrl(placement)}
          target="_blank"
          rel="noopener"
          onClick={onOpen}
          title={title}
          aria-label={t("nav.pennyMealsShort")}
          className={`flex min-w-0 flex-1 items-center gap-3 py-2.5 pl-3 ${collapsed ? "md:justify-center md:px-2" : ""}`}
        >
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white">
            <img src={MARK_SRC} alt="" className="h-[22px] w-[22px] object-contain" />
          </span>
          <span className={`min-w-0 flex-1 ${collapsed ? "md:hidden" : ""}`}>
            <span className="block text-[13px] font-bold leading-4 text-white">{t("nav.pennyMealsShort")}</span>
            <span className="mt-0.5 block text-[11px] leading-[14px] text-primary-200 dark:text-dark-300">{isMember ? t("nav.pennyMealsSidebarMember") : t("nav.pennyMealsSidebarFree")}</span>
          </span>
        </a>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t("nav.pennyMealsDismiss")}
          title={t("nav.pennyMealsDismiss")}
          className={`mr-1 flex h-8 w-8 flex-shrink-0 items-center justify-center self-start rounded-md text-primary-200 hover:bg-white/10 hover:text-white ${collapsed ? "md:hidden" : ""}`}
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
